import React, { useMemo } from 'react'
import MultiPicklist, { MultiPicklistOption } from './MultiPicklist'
import useListAction from 'core/hooks/useListAction'
import { listMngmRoles } from 'app/plugins/account/components/userManagement/roles/new-actions'
import { makeManagementRolesSelector } from 'app/plugins/account/components/userManagement/roles/selectors'
import { useSelector } from 'react-redux'
import withFormContext, {
  PropsWithFormContext,
} from 'core/components/validatedForm/withFormContext'

export interface RoleOption {
  id: string
  name: string
}

interface Props {
  id?: string
  label?: string
  className?: string
  value?: RoleOption[]
  onChange?: (values: RoleOption[]) => void
  required?: boolean
  error?: string
  errorMessage?: string
  disabled?: boolean
}

function RolesMultiPicklistBase({
  id,
  label = 'Roles',
  className,
  value = [],
  onChange,
  required,
  error,
  errorMessage,
  disabled = false,
}: Props) {
  const { loading } = useListAction(listMngmRoles)
  const roles = useSelector(makeManagementRolesSelector())
  const roleOptions: MultiPicklistOption<RoleOption>[] = useMemo(() => {
    return roles.map((role) => ({
      label: role.displayName.replace('User', ''),
      value: {
        id: role.id,
        name: role.displayName.replace('User', ''),
      },
    }))
  }, [roles])

  const isEqual = (a: RoleOption, b: RoleOption) => a.id === b.id

  const getOptionLabel = (option: RoleOption) => option.name

  // Use errorMessage from form context if available, otherwise use error prop
  const displayError = errorMessage || error

  return (
    <MultiPicklist<RoleOption>
      id={id}
      label={label && required ? `${label} *` : label}
      className={className}
      value={value}
      onChange={onChange}
      options={roleOptions}
      error={displayError}
      disabled={disabled}
      loading={loading}
      placeholder="Select Roles"
      getOptionLabel={getOptionLabel}
      isEqual={isEqual}
    />
  )
}

// Non-validated version for standalone use
export function RolesMultiPicklist(props: Props) {
  return <RolesMultiPicklistBase {...props} />
}

// Validated version wrapped with form context
export default withFormContext(RolesMultiPicklistBase) as React.FC<
  PropsWithFormContext<RoleOption[], Props>
>
